/**
 * Fox — Glass-Jaw predator NPC in the Overgrown Conservatory.
 *
 * Canon PR 3 (2026-04-14 companion book): the fox is cycle-phase-aware
 * rather than day/night-scheduled. The canonical framing is that the
 * conservatory's Incubation/Irrigation cycle determines whether the fox
 * hunts or is sensory-deprived — in Incubation (hot, dry, bright) the
 * glass-jaw predator echolocates and hunts freely; in Irrigation (cool,
 * misty, shadowed) the mist blinds and muffles its senses and it becomes
 * a "confused prowl." A cycle flip startles the fox for 400ms regardless
 * of direction (the environment changes around it).
 *
 * Until Canon PR 2 introduces the `CONSERVATORY_CYCLE_*` events, the
 * StealthEscort puzzle still listens to `DAY_NIGHT_CHANGED` and maps
 * `isNight=true` → Incubation (hunting) and `isNight=false` → Irrigation
 * (blinded). That mapping is deliberately inverted from "foxes sleep in
 * daylight": the real framing is that *night is when the Incubation
 * heat-bloom comes out*, not that it's dark.
 *
 * Behaviour matrix per phase:
 * - **hunting**: patrol + chase logic active, detection radius 9u,
 *   glass jaw emissive bright, red alert light flickering. Damage on
 *   contact fires `FOX_HUNTING` + eventual `PLAYER_DAMAGED` via the
 *   puzzle layer.
 * - **blinded**: `canDetect` always returns false, move speed half
 *   patrol, glass jaw muted, eyes dim. Contact still happens but is a
 *   nudge, not a damage event.
 * - **startled**: detection disabled, movement frozen, eyes pulse,
 *   `FOX_STARTLED` event fires once. Auto-returns to the prior phase
 *   after the configured duration.
 *
 * Shadow Slip still blocks detection in the hunting phase — canon says
 * shadows literally hide Minna from the glass jaw. Chronos Bubble still
 * scales movement in both phases.
 */
import { Scene } from "@babylonjs/core/scene";
import foxMotion from "../../../assets/models-src/npcs/glass_jaw_fox_anim.json";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { NPC, NPCConfig } from "./NPC";
import { GroundedNpcPresentation, type GroundedNpcOptions } from "./GroundedNpcPresentation";
import { NPC_TUNING } from "../../config/GameConfig";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import {
  FoxSightCone,
  FoxSightConeConfig,
  FoxSightConeState,
} from "./FoxSightCone";

/** Cycle-phase-aware fox behaviour mode. */
export type FoxPhase = "hunting" | "blinded" | "startled";

/** How long a cycle transition startles the fox. */
const STARTLE_DURATION_MS = 400;

// ---------------------------------------------------------------------------
// Static Color3 palettes — allocated ONCE at module load, never mutated.
// applyVisualState copies from these into material slots via .set()/.copyFrom()
// instead of allocating `new Color3` or `Color3.FromHexString` every frame.
// ---------------------------------------------------------------------------

// blinded phase
const _C_BLINDED_MAT_DIFF    = new Color3(0.38, 0.34, 0.36);
const _C_BLINDED_MAT_EMIS    = new Color3(0.02, 0.02, 0.03);
const _C_BLINDED_FUR_DIFF    = new Color3(0.3, 0.28, 0.32);
const _C_BLINDED_JAW_DIFF    = Color3.FromHexString("#8faab0");
const _C_BLINDED_JAW_EMIS    = new Color3(0.01, 0.03, 0.04);
const _C_BLINDED_EYE_EMIS    = new Color3(0.02, 0.03, 0.04);

// startled phase (base — pulse is applied via .set() with computed values)
const _C_STARTLED_MAT_DIFF   = Color3.FromHexString("#b76738");
const _C_STARTLED_MAT_EMIS   = new Color3(0.08, 0.04, 0.01);
const _C_STARTLED_FUR_DIFF   = Color3.FromHexString("#d9884a");
const _C_STARTLED_FUR_EMIS   = new Color3(0.04, 0.02, 0.01);
const _C_STARTLED_JAW_DIFF   = Color3.FromHexString("#c7f2fb");

// hunting — following
const _C_FOLLOWING_MAT_DIFF  = Color3.FromHexString("#d4622f");
const _C_FOLLOWING_MAT_EMIS  = new Color3(0.18, 0.05, 0.01);
const _C_FOLLOWING_FUR_DIFF  = Color3.FromHexString("#f08a43");
const _C_FOLLOWING_FUR_EMIS  = new Color3(0.08, 0.03, 0.01);
const _C_FOLLOWING_JAW_DIFF  = Color3.FromHexString("#9ce8f4");

// hunting — frantic
const _C_FRANTIC_MAT_DIFF    = Color3.FromHexString("#c76d3a");
const _C_FRANTIC_MAT_EMIS    = new Color3(0.12, 0.05, 0.01);
const _C_FRANTIC_FUR_DIFF    = Color3.FromHexString("#e59d5f");
const _C_FRANTIC_FUR_EMIS    = new Color3(0.05, 0.03, 0.01);
const _C_FRANTIC_JAW_DIFF    = Color3.FromHexString("#9fe4f0");

// hunting — patrol (default)
const _C_PATROL_MAT_DIFF     = Color3.FromHexString("#a95b2b");
const _C_PATROL_MAT_EMIS     = new Color3(0.04, 0.015, 0.005);
const _C_PATROL_FUR_DIFF     = Color3.FromHexString("#d88d49");
const _C_PATROL_FUR_EMIS     = new Color3(0.02, 0.01, 0.005);
const _C_PATROL_JAW_DIFF     = Color3.FromHexString("#95d3db");
const _C_PATROL_JAW_EMIS     = new Color3(0.03, 0.08, 0.09);
const _C_PATROL_EYE_EMIS     = new Color3(0.68, 0.3, 0.1);

/** Reusable black (read-only). */
const _C_BLACK = new Color3(0, 0, 0);

const FOX_MODEL_SCALE = 0.62;

export class Fox extends NPC {
  private presentation: GroundedNpcPresentation | null = null;
  override get species(): string { return "fox"; }

  private alertLight: PointLight;
  private foxPhase: FoxPhase = "blinded";
  /** Phase to restore to after a startle window elapses. */
  private prevPhase: FoxPhase = "blinded";
  /** Remaining startle time in seconds; 0 = not startled. */
  private startleRemaining = 0;
  private timeScaleMultiplier = 1.0;
  private glassJawMat!: StandardMaterial;
  private furAccentMat!: StandardMaterial;
  private eyeMat!: StandardMaterial;
  private accentMeshes: Mesh[] = [];

  /** Accessibility visualiser — the Option E hybrid sight cone. Always
   *  instantiated; per-frame state is driven by `syncSightCone()`. */
  private sightCone: FoxSightCone;
  /** Last stealthed-target world position passed into `syncSightCone()`.
   *  Held as a field so `update()` can re-issue the latest frame payload
   *  without the caller having to duplicate it on every per-NPC loop. */
  private lastStealthedTargetPos: Vector3 | null = null;

  constructor(
    scene: Scene,
    key: string,
    position: Vector3,
    patrolPoints: number[] = [],
    sightConeConfig?: FoxSightConeConfig,
    visuals?: GroundedNpcOptions,
  ) {
    const config: NPCConfig = {
      key,
      position,
      height: 1.0,
      radius: 0.3,
      color: '#cc4422',
      behavior: {
        initialState: 'idle',
        calmRadius: NPC_TUNING.FOX.ALERT_RADIUS,
        calmThreshold: NPC_TUNING.FOX.CALM_THRESHOLD,
        calmRate: 15,
        calmDecay: 20,
        patrolPoints,
        patrolSpeed: NPC_TUNING.FOX.PATROL_SPEED,
        followSpeed: NPC_TUNING.FOX.CHASE_SPEED,
        followDistance: 1.5,
      },
      modelPath: visuals?.modelPath ?? "/assets/models/npcs/glass_jaw_fox.glb",
      managedModelPlacement: !!visuals,
      modelScale: FOX_MODEL_SCALE,
      modelOffset: new Vector3(0.02, -0.38, 0),
      modelRotationY: Math.PI / 2,
    };

    super(scene, config);
    this.createVisualShell();
    // Presentation cues share the skin's support and authored head anchor;
    // AI and collision continue to use the original logical root.
    const supportOffset = visuals ? visuals.floorY - this.mesh.position.y : 0;
    const eyeHeight = sightConeConfig?.eyeHeight ?? (visuals
      ? supportOffset + (foxMotion.anatomy.head[2] - foxMotion.anatomy.floor_z) * FOX_MODEL_SCALE
      : 0.4);

    // Alert light — red glow when hunting/frantic
    this.alertLight = new PointLight(`fox_${key}_alert`, Vector3.Zero(), scene);
    this.alertLight.diffuse = new Color3(1.0, 0.15, 0.0);
    this.alertLight.specular = Color3.Black();
    this.alertLight.intensity = 0;
    this.alertLight.range = 4;
    this.alertLight.parent = this.mesh;
    this.alertLight.position = new Vector3(0, visuals ? eyeHeight : 0.7, 0);

    // Start blinded (Irrigation default).
    this.syncVisualVisibility(this.presentation ? 1 : 0.5);
    this.applyVisualState(0);

    // Accessibility sight-cone visualiser — the Option E hybrid treatment
    // spec'd in `docs/specs/SPEC-fox-sight-cone.md`. Instantiated up-front
    // so its materials/meshes can be reused every frame; state is driven
    // by the owning puzzle via `syncSightCone()` (and falls back to the
    // fox's own phase/behaviour state when the caller doesn't supply one).
    // The source head anchor and the same measured support used by the skin
    // locate presentation cues without changing the AI/detection root.
    this.sightCone = new FoxSightCone(scene, this.mesh, visuals ? {
      ...sightConeConfig,
      groundHeight: supportOffset + .04,
      eyeHeight,
    } : sightConeConfig);
    if (visuals) {
      this.presentation = new GroundedNpcPresentation(this.mesh, this.accentMeshes, FOX_MODEL_SCALE, visuals);
      this.syncVisualVisibility(1);
    }
  }

  /**
   * Enter the hunting phase (called when the cycle flips to Incubation).
   * Any in-flight startle from the flip that preceded this call is honoured:
   * the fox startles first, then the startle timer restores this hunting
   * phase as its `prevPhase`. That's why `startleFor` is called before
   * `foxPhase` is rewritten.
   */
  enterHunting(): void {
    if (this.foxPhase === "startled") {
      // Already startled — make sure we land in hunting when the timer fires.
      this.prevPhase = "hunting";
      return;
    }
    if (this.foxPhase === "hunting") return;
    this.foxPhase = "hunting";
    this.prevPhase = "hunting";
    this.behavior.setState("patrol");
    this.syncVisualVisibility(1);
    this.applyVisualState(0);
  }

  /**
   * Enter the blinded phase (called when the cycle flips to Irrigation).
   * Preserves any active startle: the restore target becomes blinded.
   */
  enterBlinded(): void {
    if (this.foxPhase === "startled") {
      this.prevPhase = "blinded";
      return;
    }
    if (this.foxPhase === "blinded") return;
    this.foxPhase = "blinded";
    this.prevPhase = "blinded";
    this.behavior.setState("idle");
    this.syncVisualVisibility(this.presentation ? 1 : 0.5);
    this.alertLight.intensity = 0;
    this.applyVisualState(0);
  }

  /**
   * Freeze the fox for a brief startle window (default per spec: 400ms)
   * when the cycle transitions. Emits `FOX_STARTLED` once on entry and
   * restores `prevPhase` when the timer elapses.
   */
  startleFor(durationMs: number = STARTLE_DURATION_MS): void {
    // Don't stack startles — if we're already startled, extend the window.
    const seconds = Math.max(0, durationMs) / 1000;
    if (this.foxPhase === "startled") {
      this.startleRemaining = Math.max(this.startleRemaining, seconds);
      return;
    }
    this.prevPhase = this.foxPhase;
    this.foxPhase = "startled";
    this.startleRemaining = seconds;
    this.behavior.setState("idle");
    this.alertLight.intensity = 0;
    this.applyVisualState(0);
    gameEvents.emit(GameEvents.FOX_STARTLED, { foxKey: this.npcKey });
  }

  /** The fox's canonical cycle-phase state. */
  get phase(): FoxPhase {
    return this.foxPhase;
  }

  /**
   * True when the fox cannot perceive or pursue targets. Used by the
   * escort puzzle to skip per-frame detection work on inert foxes.
   */
  get isDormant(): boolean {
    return this.foxPhase !== "hunting";
  }

  /** Apply time scale from Chronos Bubble (< 1 = slowed). */
  setTimeScale(scale: number): void {
    this.timeScaleMultiplier = scale;
  }

  resetTimeScale(): void {
    this.timeScaleMultiplier = 1.0;
  }

  /**
   * Check if the fox can detect a target.
   * Only returns true in the hunting phase. Shadow Slip still blocks
   * detection even while hunting — canon says the mist and shadow both
   * hide the cat from the glass jaw's echolocation.
   */
  canDetect(targetX: number, targetY: number, isStealthed: boolean): boolean {
    if (this.foxPhase !== "hunting") return false;
    if (isStealthed) return false;

    const dx = this.mesh.position.x - targetX;
    const dy = this.mesh.position.y - targetY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    return dist <= NPC_TUNING.FOX.ALERT_RADIUS;
  }

  /** Set a hunt target (e.g., an opossum detected). */
  startHunting(target: { x: number; y: number }): void {
    if (this.foxPhase !== "hunting") return;
    const wasHunting = this.behavior.state === "following";
    this.behavior.followTarget = target;
    if (!wasHunting) {
      this.behavior.setState("following");
      gameEvents.emit(GameEvents.FOX_HUNTING, { foxKey: this.npcKey });
    }
  }

  /** Go back to patrol after losing target. */
  resumePatrol(): void {
    this.behavior.followTarget = undefined;
    this.behavior.setState('patrol');
  }

  /**
   * Override base updateBehavior to apply time-scale multiplier.
   * The fox moves slower when inside a Chronos Bubble. Blinded foxes
   * still drift on patrol at a reduced pace (spec: ~50% of hunting
   * speed) so the player sees the fox "prowling confused" rather than
   * standing perfectly still. Startled foxes are fully frozen.
   */
  override updateBehavior(
    deltaSeconds: number,
    purring: boolean,
    purrX: number,
    purrY: number,
  ): void {
    if (this.foxPhase === "startled") return;
    const speedScale =
      this.foxPhase === "blinded" ? 0.5 : 1.0;
    super.updateBehavior(
      deltaSeconds * this.timeScaleMultiplier * speedScale,
      purring,
      purrX,
      purrY,
    );
  }

  protected override onVisualModelLoaded(): void {
    if (this.visualModel) this.presentation?.adopt(this.visualModel);
  }

  override update(time: number, deltaSeconds: number): void {
    this.updateState(time, deltaSeconds);
    this.presentation?.setPose(this.foxPhase === "startled" ? "startled" : null);
    this.presentation?.update(deltaSeconds);
  }

  private updateState(time: number, _deltaSeconds: number): void {
    // Tick the startle timer regardless of phase so we eventually restore.
    if (this.foxPhase === "startled") {
      this.startleRemaining -= _deltaSeconds;
      if (this.startleRemaining <= 0) {
        this.startleRemaining = 0;
        // Route restore through the enter* helpers so the behaviour state
        // and visuals land in a consistent place.
        if (this.prevPhase === "hunting") {
          this.foxPhase = "blinded"; // force-distinct so enterHunting runs
          this.enterHunting();
        } else {
          this.foxPhase = "hunting";
          this.enterBlinded();
        }
      } else {
        // Pulse the eye glow while frozen so the "yip" reads visually.
        this.applyVisualState(time);
        this.sightCone.setState({
          state: this.resolveSightConeState(),
          time,
          stealthedTargetPos: this.lastStealthedTargetPos ?? undefined,
        });
        return;
      }
    }

    if (this.foxPhase === "blinded") {
      // Keep driving the patrol loop at half speed so the confused prowl
      // reads as ongoing motion, not a sleeping fox. Minna can still walk
      // past — canDetect is hard-gated to hunting.
      this.updateBehavior(_deltaSeconds, false, 0, 0);
      this.alertLight.intensity = 0;
      this.applyVisualState(time);
      this.sightCone.setState({
        state: this.resolveSightConeState(),
        time,
        stealthedTargetPos: this.lastStealthedTargetPos ?? undefined,
      });
      return;
    }

    // Hunting path: advance patrol / chase movement every frame.
    this.updateBehavior(_deltaSeconds, false, 0, 0);

    const isAlert =
      this.behavior.state === "frantic" || this.behavior.state === "following";
    this.alertLight.intensity = isAlert ? 2.0 + Math.sin(time * 6) * 0.5 : 0;

    this.applyVisualState(time);

    // Always refresh the sight cone so the visualiser tracks movement,
    // facing, and state changes. The stealth-protected "failure bite" is
    // driven off the cached lastStealthedTargetPos set by syncSightCone()
    // from the owning puzzle.
    this.sightCone.setState({
      state: this.resolveSightConeState(),
      time,
      stealthedTargetPos: this.lastStealthedTargetPos ?? undefined,
    });
  }

  /**
   * Feed a stealthed-target probe into the sight-cone visualiser. Called
   * by puzzle modules that own the fox (NectarEscort, ShadowRelay) so the
   * "cone fails to bite the cat" teaching beat renders when Shadow Slip is
   * active. Passing `null` clears the cached probe (e.g. the cat stepped
   * out of the shadow zone).
   */
  syncSightCone(minnaPos: Vector3 | null, shadowSlipActive: boolean): void {
    if (minnaPos && shadowSlipActive) {
      this.lastStealthedTargetPos = minnaPos.clone();
    } else {
      this.lastStealthedTargetPos = null;
    }
  }

  /** Expose the visualiser for inspection (tests + scene QA). */
  get sightConeVisualiser(): FoxSightCone {
    return this.sightCone;
  }

  override dispose(): void {
    this.presentation?.dispose();
    this.alertLight.dispose();
    this.glassJawMat.dispose();
    this.furAccentMat.dispose();
    this.eyeMat.dispose();
    this.sightCone.dispose();
    super.dispose();
  }

  /**
   * Map the fox's internal state into the sight-cone visualiser's four
   * canonical states. Priority order:
   *   1. If the caller supplied a stealthed-target probe AND the fox is
   *      currently hunting, force "stealth-protected" so the cone visibly
   *      fails where the cat stands.
   *   2. Otherwise, hunting maps to `hunting`, any alert-ish state
   *      (frantic / actively chasing) to `alerted`, and the default is
   *      `patrol`.
   */
  private resolveSightConeState(): FoxSightConeState {
    if (this.foxPhase !== "hunting") {
      // Blinded / startled: cone still renders at patrol intensity so the
      // player always has a footprint tell. Going fully dark on blinded
      // would break the "every player sees the danger stripe" contract.
      return "patrol";
    }

    if (
      this.lastStealthedTargetPos &&
      this.behavior.state === "following"
    ) {
      return "stealth-protected";
    }

    if (this.behavior.state === "following") return "hunting";
    if (this.behavior.state === "frantic") return "alerted";
    return "patrol";
  }

  private createVisualShell(): void {
    this.furAccentMat = new StandardMaterial(`fox_${this.npcKey}_furAccent`, this.scene);
    this.furAccentMat.specularColor = Color3.Black();

    this.glassJawMat = new StandardMaterial(`fox_${this.npcKey}_glassJaw`, this.scene);
    this.glassJawMat.specularColor = Color3.FromHexString("#b7f7ff").scale(0.2);
    this.glassJawMat.alpha = 0.72;

    this.eyeMat = new StandardMaterial(`fox_${this.npcKey}_eyeGlow`, this.scene);
    this.eyeMat.diffuseColor = Color3.Black();
    this.eyeMat.specularColor = Color3.Black();

    const head = MeshBuilder.CreateSphere(
      `fox_${this.npcKey}_head`,
      { diameterX: 0.56, diameterY: 0.44, diameterZ: 0.42, segments: 10 },
      this.scene,
    );
    head.parent = this.mesh;
    head.position.set(0.33, 0.16, 0);
    head.material = this.material;
    this.accentMeshes.push(head);

    const ruff = MeshBuilder.CreateSphere(
      `fox_${this.npcKey}_ruff`,
      { diameterX: 0.92, diameterY: 0.72, diameterZ: 0.56, segments: 10 },
      this.scene,
    );
    ruff.parent = this.mesh;
    ruff.position.set(-0.05, 0.08, 0);
    ruff.material = this.furAccentMat;
    this.accentMeshes.push(ruff);

    const snout = MeshBuilder.CreateBox(
      `fox_${this.npcKey}_snout`,
      { width: 0.34, height: 0.16, depth: 0.16 },
      this.scene,
    );
    snout.parent = this.mesh;
    snout.position.set(0.58, 0.05, 0);
    snout.material = this.furAccentMat;
    this.accentMeshes.push(snout);

    for (const [index, side] of [-1, 1].entries()) {
      const ear = MeshBuilder.CreateBox(
        `fox_${this.npcKey}_ear_${index}`,
        { width: 0.16, height: 0.22, depth: 0.06 },
        this.scene,
      );
      ear.parent = this.mesh;
      ear.position.set(0.18, 0.48, side * 0.16);
      ear.rotation.z = side * 0.22;
      ear.material = this.furAccentMat;
      this.accentMeshes.push(ear);

      const eye = MeshBuilder.CreateSphere(
        `fox_${this.npcKey}_eye_${index}`,
        { diameter: 0.07, segments: 6 },
        this.scene,
      );
      eye.parent = this.mesh;
      eye.position.set(0.46, 0.15, side * 0.1);
      eye.material = this.eyeMat;
      this.accentMeshes.push(eye);
    }

    const jawUpper = MeshBuilder.CreateBox(
      `fox_${this.npcKey}_jaw_upper`,
      { width: 0.42, height: 0.12, depth: 0.2 },
      this.scene,
    );
    jawUpper.parent = this.mesh;
    jawUpper.position.set(0.72, 0.1, 0);
    jawUpper.material = this.glassJawMat;
    this.accentMeshes.push(jawUpper);

    const jawLower = MeshBuilder.CreateBox(
      `fox_${this.npcKey}_jaw_lower`,
      { width: 0.38, height: 0.1, depth: 0.18 },
      this.scene,
    );
    jawLower.parent = this.mesh;
    jawLower.position.set(0.72, -0.02, 0);
    jawLower.rotation.z = 0.08;
    jawLower.material = this.glassJawMat;
    this.accentMeshes.push(jawLower);

    const tail = MeshBuilder.CreateCylinder(
      `fox_${this.npcKey}_tail`,
      { height: 0.82, diameterTop: 0.08, diameterBottom: 0.16, tessellation: 10 },
      this.scene,
    );
    tail.parent = this.mesh;
    tail.position.set(-0.46, 0.18, 0);
    tail.rotation.z = -1.05;
    tail.material = this.furAccentMat;
    this.accentMeshes.push(tail);
  }

  private applyVisualState(time: number): void {
    if (this.visualModel) {
      for (const mesh of this.accentMeshes) {
        mesh.isVisible = false;
      }
      return;
    }

    if (this.foxPhase === "blinded") {
      // Confused prowl: cool muted fur, glass jaw dim, eyes nearly dark.
      // Uses pre-allocated module-level Color3 constants — no per-frame allocation.
      this.material.diffuseColor.copyFrom(_C_BLINDED_MAT_DIFF);
      this.material.emissiveColor.copyFrom(_C_BLINDED_MAT_EMIS);
      this.furAccentMat.diffuseColor.copyFrom(_C_BLINDED_FUR_DIFF);
      this.furAccentMat.emissiveColor.copyFrom(_C_BLACK);
      this.glassJawMat.diffuseColor.copyFrom(_C_BLINDED_JAW_DIFF);
      this.glassJawMat.emissiveColor.copyFrom(_C_BLINDED_JAW_EMIS);
      this.eyeMat.emissiveColor.copyFrom(_C_BLINDED_EYE_EMIS);
      return;
    }

    if (this.foxPhase === "startled") {
      // Frozen mid-pivot — eyes pulse brightly, glass jaw briefly flares.
      // Animated values use .set() on existing Color3 objects — no allocation.
      const pulse = 0.5 + Math.abs(Math.sin(time * 20)) * 0.5;
      this.material.diffuseColor.copyFrom(_C_STARTLED_MAT_DIFF);
      this.material.emissiveColor.copyFrom(_C_STARTLED_MAT_EMIS);
      this.furAccentMat.diffuseColor.copyFrom(_C_STARTLED_FUR_DIFF);
      this.furAccentMat.emissiveColor.copyFrom(_C_STARTLED_FUR_EMIS);
      this.glassJawMat.diffuseColor.copyFrom(_C_STARTLED_JAW_DIFF);
      this.glassJawMat.emissiveColor.set(0.2 * pulse, 0.35 * pulse, 0.45 * pulse);
      this.eyeMat.emissiveColor.set(1.0 * pulse, 0.6 * pulse, 0.1 * pulse);
      return;
    }

    for (const mesh of this.accentMeshes) {
      mesh.isVisible = true;
    }

    const alertPulse =
      this.behavior.state === "following" || this.behavior.state === "frantic"
        ? 0.24 + Math.max(0, Math.sin(time * 8)) * 0.18
        : 0.06;

    switch (this.behavior.state) {
      case 'following':
        this.material.diffuseColor.copyFrom(_C_FOLLOWING_MAT_DIFF);
        this.material.emissiveColor.copyFrom(_C_FOLLOWING_MAT_EMIS);
        this.furAccentMat.diffuseColor.copyFrom(_C_FOLLOWING_FUR_DIFF);
        this.furAccentMat.emissiveColor.copyFrom(_C_FOLLOWING_FUR_EMIS);
        this.glassJawMat.diffuseColor.copyFrom(_C_FOLLOWING_JAW_DIFF);
        this.glassJawMat.emissiveColor.set(0.08 + alertPulse, 0.18 + alertPulse, 0.2 + alertPulse);
        this.eyeMat.emissiveColor.set(0.9, 0.38 + alertPulse, 0.16);
        break;
      case 'frantic':
        this.material.diffuseColor.copyFrom(_C_FRANTIC_MAT_DIFF);
        this.material.emissiveColor.copyFrom(_C_FRANTIC_MAT_EMIS);
        this.furAccentMat.diffuseColor.copyFrom(_C_FRANTIC_FUR_DIFF);
        this.furAccentMat.emissiveColor.copyFrom(_C_FRANTIC_FUR_EMIS);
        this.glassJawMat.diffuseColor.copyFrom(_C_FRANTIC_JAW_DIFF);
        this.glassJawMat.emissiveColor.set(0.12 + alertPulse, 0.18 + alertPulse, 0.2 + alertPulse);
        this.eyeMat.emissiveColor.set(0.85, 0.34 + alertPulse, 0.12);
        break;
      case 'patrol':
      default:
        this.material.diffuseColor.copyFrom(_C_PATROL_MAT_DIFF);
        this.material.emissiveColor.copyFrom(_C_PATROL_MAT_EMIS);
        this.furAccentMat.diffuseColor.copyFrom(_C_PATROL_FUR_DIFF);
        this.furAccentMat.emissiveColor.copyFrom(_C_PATROL_FUR_EMIS);
        this.glassJawMat.diffuseColor.copyFrom(_C_PATROL_JAW_DIFF);
        this.glassJawMat.emissiveColor.copyFrom(_C_PATROL_JAW_EMIS);
        this.eyeMat.emissiveColor.copyFrom(_C_PATROL_EYE_EMIS);
        break;
    }
  }
}
